/**
 * BEND (NP and Core) provider configurations.
 * These providers use a simplified calendar setup flow where operations
 * handles availability setup after OAuth authorization.
 */

import { BEND_CORE_DA_CALENDAR, BEND_CORE_LT_CALENDAR, BEND_NP_DA_CALENDAR, BEND_NP_LT_CALENDAR } from './calendars'
import { RoleConfig } from './types'

/**
 * Core DA (1099) provider configuration.
 * Direct Access Core Care Coordinator - 1099 contractor.
 * Only calendar navigation is shown for BEND providers.
 */
export const DA_CORECARE_COORDINATOR: RoleConfig = {
  bcPlatform: true,
  videoSessionsEnabled: false,
  medicationEnabled: false,
  zendeskWidget: false,
  directAccessClientList: false,
  requestPayment: false,
  eligibilityChecker: false,
  showOverdueMessageFlag: true,
  showInPersonPrompt: true,
  smartSchedulingEnabled: true,
  hidePerformanceMetrics: true,
  showCloseSessionBanner: true,
  inProductCalendar: BEND_CORE_DA_CALENDAR,
  showCareInsights: false,
  hideTodoPage: true,
  hideClientsNav: true,
  hideMessagesNav: true,
  hideDocumentsNav: true,
  hideSearchNav: true,
  hideSettingsNav: true,
  utilizationEnabled: false,
  helpCenterUrl: 'https://bend-providers.zendesk.com/hc/en-us/',
}

/**
 * Core LT (W2) provider configuration.
 * Lyra Therapy Core Care Coordinator - W2 employee.
 * Only calendar navigation is shown for BEND providers.
 */
export const LT_CORECARE_COORDINATOR: RoleConfig = {
  bcPlatform: true,
  videoSessionsEnabled: false,
  medicationEnabled: false,
  zendeskWidget: false,
  directAccessClientList: false,
  requestPayment: false,
  eligibilityChecker: false,
  showOverdueMessageFlag: true,
  showInPersonPrompt: true,
  smartSchedulingEnabled: true,
  hidePerformanceMetrics: true,
  showCloseSessionBanner: true,
  inProductCalendar: BEND_CORE_LT_CALENDAR,
  showCareInsights: false,
  hideTodoPage: true,
  hideClientsNav: true,
  hideMessagesNav: true,
  hideDocumentsNav: true,
  hideSearchNav: true,
  hideSettingsNav: true,
  utilizationEnabled: false,
  helpCenterUrl: 'https://bend-providers.zendesk.com/hc/en-us/',
}

/**
 * NP DA (1099) provider configuration.
 * Direct Access Neuropsych Tester - 1099 contractor.
 * The Clients nav is additionally gated by the `npTestingPlans` flag in Header.
 */
export const DA_NPTESTER: RoleConfig = {
  bcPlatform: true,
  videoSessionsEnabled: false,
  medicationEnabled: false,
  zendeskWidget: false,
  directAccessClientList: false,
  requestPayment: false,
  eligibilityChecker: false,
  showOverdueMessageFlag: true,
  showInPersonPrompt: true,
  smartSchedulingEnabled: true,
  hidePerformanceMetrics: true,
  showCloseSessionBanner: true,
  inProductCalendar: BEND_NP_DA_CALENDAR,
  showCareInsights: false,
  hideTodoPage: true,
  hideClientsNav: false,
  hideMessagesNav: true,
  hideDocumentsNav: true,
  hideSearchNav: true,
  hideSettingsNav: true,
  utilizationEnabled: false,
  helpCenterUrl: 'https://bend-providers.zendesk.com/hc/en-us/',
}

/**
 * NP LT (W2) provider configuration.
 * Lyra Therapy Neuropsych Tester - W2 employee.
 * The Clients nav is additionally gated by the `npTestingPlans` flag in Header.
 */
export const LT_NPTESTER: RoleConfig = {
  bcPlatform: true,
  videoSessionsEnabled: false,
  medicationEnabled: false,
  zendeskWidget: false,
  directAccessClientList: false,
  requestPayment: false,
  eligibilityChecker: false,
  showOverdueMessageFlag: true,
  showInPersonPrompt: true,
  smartSchedulingEnabled: true,
  hidePerformanceMetrics: true,
  showCloseSessionBanner: true,
  inProductCalendar: BEND_NP_LT_CALENDAR,
  showCareInsights: false,
  hideTodoPage: true,
  hideClientsNav: false,
  hideMessagesNav: true,
  hideDocumentsNav: true,
  hideSearchNav: true,
  hideSettingsNav: true,
  utilizationEnabled: false,
  helpCenterUrl: 'https://bend-providers.zendesk.com/hc/en-us/',
}

/**
 * NP testing psychologists and neuropsychologists set new-client capacity (ICE-7497);
 * psychometrists and care coordinators keep the plain NP configs.
 */
export const DA_NP_CAPACITY_PROVIDER: RoleConfig = { ...DA_NPTESTER, showCalendarCapacity: true }
export const LT_NP_CAPACITY_PROVIDER: RoleConfig = { ...LT_NPTESTER, showCalendarCapacity: true }

/**
 * BEND NP Supervisor configuration.
 * Supervisors for Neuropsych Testers - can manage provider calendars.
 * When a supervisor selects a provider, the provider's calendar config is used.
 */
export const NPTESTER_SUPERVISOR: RoleConfig = {
  zendeskWidget: false,
  eligibilityChecker: false,
  assessmentTrackSettingsEnabled: true,
  inProductCalendar: BEND_NP_DA_CALENDAR,
  hideMessagesNav: true,
}

/**
 * BEND Core Care Coordinator Supervisor configuration.
 * Supervisors for Core Care Coordinators - can manage provider calendars.
 * When a supervisor selects a provider, the provider's calendar config is used.
 */
export const CORECARE_COORDINATOR_SUPERVISOR: RoleConfig = {
  zendeskWidget: false,
  eligibilityChecker: false,
  assessmentTrackSettingsEnabled: true,
  inProductCalendar: BEND_CORE_DA_CALENDAR,
  hideMessagesNav: true,
}
